import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import BigNumber from 'bignumber.js';
import { Copy, Check } from 'lucide-react';
import InfoHint from '../../components/common/InfoHint';
import { abbrInline } from './pocketFormat';
import { formatPriceLevel } from '../../utils/pricePrecision';

// Fire a window event so the shell (PocketCalc) can show a "Copied" toast — the
// single chokepoint every copy path (CopyVal/CopySub/PkFee/PkPnl/inputs) flows through.
const notifyCopied = (text) => { try { window.dispatchEvent(new CustomEvent('pk-copied', { detail: text })); } catch {} };

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); notifyCopied(text); return; }
  } catch {}
  const ta = document.createElement('textarea');
  ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
  document.body.appendChild(ta); ta.focus(); ta.select();
  try { document.execCommand('copy'); } catch {}
  ta.remove();
  notifyCopied(text);
};
// strip everything except the number (drop $, ×, %, commas, "R past stop", etc.)
const cleanNumber = (v) => {
  const m = String(v).replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return m ? m[0] : String(v);
};

const toneClass = (tone) => tone === 'green' ? 'text-[#54cca0]' : tone === 'red' ? 'text-[#f2767f]' : tone === 'amber' ? 'text-[#d9a441]' : tone === 'blue' ? 'text-[#5aa9e6]' : 'text-[#cdd6e1]';

export const SideBadge = ({ side }) => side ? (
  <span className={`text-[9px] font-medium px-1.5 py-px rounded ${side === 'short' ? 'text-[#f2767f] bg-[rgba(242,118,127,0.13)]' : 'text-[#54cca0] bg-[rgba(84,204,154,0.13)]'}`}>
    {side === 'short' ? 'Short' : 'Long'}
  </span>
) : null;

// shared click-to-copy behaviour (strips $,×,%,commas → raw number)
const useCopyClick = (value) => {
  const [done, setDone] = useState(false);
  const onClick = (e) => { e.stopPropagation(); copyToClipboard(cleanNumber(value)); setDone(true); setTimeout(() => setDone(false), 900); };
  return { done, onClick };
};
const CopyIcon = ({ done, size = 11 }) => (
  <span className="shrink-0 self-center w-3">
    {done ? <Check size={size} className="text-[#54cca0]" /> : <Copy size={size} className="opacity-0 group-hover:opacity-100 text-slate-500 transition-opacity" />}
  </span>
);

// plain layout row — each value inside is its OWN hover-to-copy target (CopyVal / CopySub)
const CopyRow = ({ children, className = '' }) => (
  <div className={`flex items-baseline justify-between gap-2.5 py-[2px] ${className}`}>{children}</div>
);

// --- Fast "hover to reveal full number" tooltip for abbreviated (K/M/B) values.
// Replaces the native `title` attribute: its OS-level open delay (~3-4s) isn't
// configurable and never "warms up". Cold hover opens after TIP_DELAY; re-hovering
// another tracked value within TIP_GROUP_MS of the last close opens instantly — the
// same feel native title tooltips have when skimming across elements, just fast.
const TIP_DELAY = 150;
const TIP_GROUP_MS = 600;
const TIP_W_EST = 140;
let tipLastClosedAt = 0;

const useHoverTip = () => {
  const ref = useRef(null);
  const timerRef = useRef(null);
  const [pos, setPos] = useState(null); // {top,left,below} while open, else null

  const place = useCallback(() => {
    const r = ref.current && ref.current.getBoundingClientRect();
    if (!r) return null;
    const vw = window.innerWidth, vh = window.innerHeight;
    const half = Math.min(TIP_W_EST, vw * 0.9) / 2;
    const left = Math.max(half + 8, Math.min(r.left + r.width / 2, vw - half - 8));
    const below = r.top < 40 && vh - r.bottom > 40;
    return { top: below ? r.bottom : r.top, left, below };
  }, []);

  const onEnter = () => {
    const delay = Date.now() - tipLastClosedAt < TIP_GROUP_MS ? 0 : TIP_DELAY;
    timerRef.current = setTimeout(() => setPos(place()), delay);
  };
  const onLeave = () => {
    clearTimeout(timerRef.current);
    if (pos) tipLastClosedAt = Date.now();
    setPos(null);
  };
  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { ref, pos, onEnter, onLeave };
};

const HoverTip = ({ tip, text }) => (tip.pos && typeof document !== 'undefined') ? createPortal(
  <div
    style={{
      position: 'fixed',
      top: tip.pos.below ? tip.pos.top + 6 : tip.pos.top - 6,
      left: tip.pos.left,
      transform: tip.pos.below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
      zIndex: 9999,
      pointerEvents: 'none',
    }}
    className="pk-mono whitespace-nowrap rounded-md bg-[#11161f] border border-white/10 px-1.5 py-1 text-[11px] text-[#eef3f8] shadow-xl"
  >
    {text}
  </div>,
  document.body
) : null;

// a single white value that is its own hover-to-copy target (copies only its number)
const CopyVal = ({ disp, full, cls = '' }) => {
  const onClick = (e) => { e.stopPropagation(); copyToClipboard(cleanNumber(full)); };
  const tip = useHoverTip();
  return (
    <span ref={tip.ref} onClick={onClick} onMouseEnter={tip.onEnter} onMouseLeave={tip.onLeave}
      className={`pk-mono cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-white/[0.06] ${cls}`}>
      {disp}
      <HoverTip tip={tip} text={String(full)} />
    </span>
  );
};

// gray secondary rendered AFTER the value (no slash); individually click-to-copy
// (stops propagation so it copies ITS number, not the row's main value).
const CopySub = ({ text, sm }) => {
  const tip = useHoverTip();
  if (text == null || text === false) return null;
  const onClick = (e) => { e.stopPropagation(); copyToClipboard(cleanNumber(text)); };
  return (
    <span ref={tip.ref} onClick={onClick} onMouseEnter={tip.onEnter} onMouseLeave={tip.onLeave}
      className={`pk-mono text-[#5b6573] ${sm ? 'text-[10px]' : 'text-[11px]'} whitespace-nowrap cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-white/[0.06]`}>
      {abbrInline(text)}
      <HoverTip tip={tip} text={String(text)} />
    </span>
  );
};
// a value may be a plain string or a { disp, full } money object (abbreviated display + full copy)
const resolve = (value) => (value && typeof value === 'object') ? value : { disp: value, full: value };

// --- Inputs: compact 2-column grid, "Label:" colon style (keeps the text aesthetic) ---
export const PkInputGrid = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-3 gap-y-px">{children}</div>
);
export const PkField = ({ label, children }) => (
  <div className="flex items-baseline text-[13px] py-[2px] min-w-0">
    <span className="text-[#7a8492] shrink-0 mr-1.5">{label}:</span>
    {children}
  </div>
);
// A grid cell that opens Settings instead of editing inline (e.g. Fee).
export const PkFieldOpen = ({ label, value, onOpen }) => (
  <button onClick={onOpen} className="group flex items-baseline text-[13px] py-[2px] -mx-1 px-1 rounded-md hover:bg-white/[0.06] transition-colors text-left min-w-0">
    <span className="text-[#7a8492] shrink-0 mr-1.5">{label}:</span>
    <span className="text-[#9aa7b4] tabular-nums truncate">{value}</span>
  </button>
);

// --- cosmo inputs: emphasised Entry | Stop, then a row of small icon-chips ---
export const PkTwoCol = ({ children }) => (
  <div className="flex gap-3">{children}</div>
);
export const PkMainField = ({ label, children, copyValue }) => {
  const { done, onClick } = useCopyClick(copyValue);
  return (
    <div className="group flex-1 flex items-baseline gap-1.5 min-w-0 text-[12px]">
      <span className="text-[#7a8492] text-[12px] shrink-0">{label}:</span>
      <span className="flex-1 min-w-0">{children}</span>
      {copyValue != null && (
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} aria-label={`Copy ${label}`} className="shrink-0 self-center">
          <CopyIcon done={done} />
        </button>
      )}
    </div>
  );
};
export const PkChipRow = ({ children }) => (
  <div className="flex items-center gap-4 mt-2 flex-wrap">{children}</div>
);
export const PkChip = ({ icon, label, title, children }) => (
  <span title={title} className="inline-flex items-center gap-1 text-[12px]">
    {icon}
    {label && <span className="text-[#5b6573] text-[12px] font-medium">{label}:</span>}
    {children}
  </span>
);

// --- cosmo fees: quiet multi-column grid (small gray) ---
export const PkFees = ({ children, cols = 3 }) => (
  <div className={`grid ${cols === 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-x-3 gap-y-px mt-0.5`}>{children}</div>
);
export const PkFee = ({ label, value, copyLabel }) => {
  const { onClick } = useCopyClick(value);
  return (
    <div onClick={onClick} aria-label={copyLabel} className="group flex items-baseline justify-between gap-1.5 cursor-pointer rounded -mx-0.5 px-0.5 hover:bg-white/[0.05]">
      <span className="text-[#5b6573] text-[9px] whitespace-nowrap">{label}</span>
      <span className="pk-mono text-[#6f7682] text-[10px]">{abbrInline(String(value))}</span>
    </div>
  );
};

// Legacy single-column input rows (kept exported for compatibility).
export const PkInputRow = ({ label, children }) => (
  <div className="flex items-baseline text-[13px] py-[2.5px]">
    <span className="text-[#7a8492] w-[84px] shrink-0">{label}:</span>
    {children}
  </div>
);
export const PkOpenRow = ({ label, value, onOpen }) => (
  <button onClick={onOpen} className="group w-full flex items-baseline text-[13px] py-[2.5px] px-2 -mx-2 rounded-md hover:bg-white/[0.06] transition-colors text-left">
    <span className="text-[#7a8492] w-[84px] shrink-0">{label}:</span>
    <span className="text-[#9aa7b4] tabular-nums">{value}</span>
  </button>
);

// subLeft → gray secondary (e.g. "Qty: 340") sits BEFORE the white value, so the
// $ sum stays on the far right. Used on the Position row; the Margin-req hero keeps
// value-then-sub.
export const PkHero = ({ label, side, value, copyLabel, sub, subLeft }) => {
  const { disp, full } = resolve(value);
  return (
    <CopyRow copyValue={full}>
      <span className="text-[#cfccc4] font-medium flex items-center gap-1.5 text-[13px] shrink-0" aria-label={copyLabel}>{label}<DirArrow side={side} /></span>
      <span className="ml-auto flex items-baseline justify-end gap-2 min-w-0">
        {subLeft && <CopySub text={sub} />}
        <CopyVal disp={disp} full={full} cls="text-right text-white text-[13px] shrink-0" />
        {!subLeft && <CopySub text={sub} />}
      </span>
    </CopyRow>
  );
};

// subLeft → gray secondary sits BEFORE (left of) the white value (white stays
// right-aligned). Used only in the Trade column; everywhere else is white-then-gray.
export const PkRow = ({ label, value, tone, info, copyLabel, sub, sm, subLeft }) => {
  const { disp, full } = resolve(value);
  const ts = sm ? 'text-[11px]' : 'text-[13px]';
  return (
    <CopyRow copyValue={full}>
      <span className={`text-[#7a8492] whitespace-nowrap flex items-center gap-1.5 ${ts} shrink-0`} aria-label={copyLabel}>
        {label}{info && <InfoHint text={info} />}
      </span>
      <span className="ml-auto flex items-baseline justify-end gap-1.5 min-w-0">
        {subLeft && <CopySub text={sub} sm={sm} />}
        <CopyVal disp={disp} full={full} cls={`text-right ${ts} shrink-0 ${toneClass(tone)}`} />
        {!subLeft && <CopySub text={sub} sm={sm} />}
      </span>
    </CopyRow>
  );
};

// merged P&L row — green +profit / red −loss, each value individually click-to-copy
export const PkPnl = ({ profit, loss }) => {
  const p = resolve(profit), l = resolve(loss);
  const strip = (s) => String(s).replace(/[−-]/g, '');
  const copy = (m) => (e) => { e.stopPropagation(); copyToClipboard(cleanNumber(m.full)); };
  const pTip = useHoverTip(), lTip = useHoverTip();
  return (
    <CopyRow copyValue={p.full}>
      <span className="text-[#7a8492] text-[13px] shrink-0">{'P&L'}</span>
      <span className="ml-auto flex items-baseline justify-end gap-1.5 min-w-0 pk-mono text-[13px]">
        <span ref={pTip.ref} onClick={copy(p)} onMouseEnter={pTip.onEnter} onMouseLeave={pTip.onLeave} className="text-[#54cca0] cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-white/[0.06]">
          +{strip(p.disp)}
          <HoverTip tip={pTip} text={String(p.full)} />
        </span>
        <span className="text-[#5b6573] text-[11px]">/</span>
        <span ref={lTip.ref} onClick={copy(l)} onMouseEnter={lTip.onEnter} onMouseLeave={lTip.onLeave} className="text-[#f2767f] cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-white/[0.06]">
          −{strip(l.disp)}
          <HoverTip tip={lTip} text={String(l.full)} />
        </span>
      </span>
    </CopyRow>
  );
};

// Standalone gray sub-line — used where a sub has no host row (e.g. Notional under
// the futures contract cards). $-amounts are abbreviated; copy keeps the full number.
export const PkSub = ({ value, copyLabel }) => {
  const { done, onClick } = useCopyClick(value);
  return (
    <div onClick={onClick} aria-label={copyLabel}
      className="group flex items-center justify-end gap-1.5 -mt-[2px] py-[1px] px-2 -mx-2 rounded-md cursor-pointer transition-colors hover:bg-white/[0.06]">
      <span className="text-[#5b6573] text-[11px] tabular-nums text-right">{abbrInline(value)}</span>
      <CopyIcon done={done} size={10} />
    </div>
  );
};

// cosmo section header: darker-gray small label + a trailing hairline (separates blocks)
export const PkSection = ({ label }) => (
  <div className="flex items-center gap-2 text-[#5b6573] text-[8px] uppercase tracking-[0.14em] font-medium mt-2.5 mb-1">
    <span className="shrink-0">{label}</span><span className="flex-1 h-px bg-white/[0.07]" />
  </div>
);

// two side-by-side detail columns (Trade | Levels), matching the cosmo-5 prototype
export const PkCols = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-4 items-start">{children}</div>
);
export const PkCol = ({ children }) => <div>{children}</div>;

// direction arrow next to the head label: green ↑ long / red ↓ short
export const DirArrow = ({ side }) => side === 'long'
  ? <span className="text-[#54cca0] text-[12px] font-bold leading-none">↑</span>
  : side === 'short' ? <span className="text-[#f2767f] text-[12px] font-bold leading-none">↓</span> : null;

// --- Multiple take-profits: the scale-out ladder shown on top of a Pocket view.
// Each row: TP · allocation · optimized target · (gross R) | Locked R | ↩ Stop R.
const rMult = (bn, sign) => {
  if (bn == null) return '--';
  try { const n = new BigNumber(bn); if (n.isNaN()) return '--'; return (sign && n.gte(0) ? '+' : '') + n.toFormat(2) + 'R'; } catch { return '--'; }
};
// Target price — instrument's real precision when known (forex/futures), else
// rounded adaptively by significant figures. See utils/pricePrecision.
const priceFmt = (v, decimals) => formatPriceLevel(v, decimals ?? null, '--');
const qtyFmtPk = (v) => { try { const n = new BigNumber(v); if (n.isNaN()) return '--'; return n.isInteger() ? n.toFormat(0) : n.toFormat(Math.min(4, Math.max(2, n.decimalPlaces()))); } catch { return '--'; } };
const cpNum = (v) => { try { copyToClipboard(cleanNumber(String(v))); } catch {} };
const TAP = 'cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-white/[0.06]';
const moneyPk = (v, sign) => {
  try {
    const n = new BigNumber(v); if (n.isNaN()) return '--';
    const s = n.lt(0) ? '−' : (sign && n.gt(0) ? '+' : '');
    const a = n.abs();
    // dp: 0 decimals once the scaled value hits 3 digits or has no fraction (130 -> "130",
    // not "130.0"); otherwise 1 decimal (1.5 -> "1.5"). Never force a trailing ".0".
    const dp = (val) => (val.gte(100) || val.mod(1).isZero()) ? 0 : 1;
    if (a.gte(1e9)) { const val = a.div(1e9); return `${s}$${val.toFormat(dp(val))}B`; }
    if (a.gte(1e6)) { const val = a.div(1e6); return `${s}$${val.toFormat(dp(val))}M`; }
    if (a.gte(1000)) { const val = a.div(1000); return `${s}$${val.toFormat(dp(val))}K`; }
    if (a.gte(100)) return `${s}$${a.toFormat(0)}`;
    return `${s}$${a.toFormat(2)}`;
  } catch { return '--'; }
};
const GRID6 = 'grid grid-cols-[1.3rem_2.3rem_minmax(0,1fr)_2rem_3rem_3rem] gap-1 items-baseline';

export const PkExits = ({ data, pricePrefix = '$', decimals = null }) => {
  const [unit, setUnit] = useState('R'); // result columns in risk (R) or dollars ($)
  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) return null;
  const runner = data.runner != null ? new BigNumber(data.runner) : new BigNumber(0);
  const NET = '#cdd6e1'; // leverage-value tone (greyish-blue), not green
  const seg = (u) => `text-[8px] px-0.5 ${unit === u ? 'text-[#cdd6e1]' : 'text-[#5b6573] hover:text-[#7a8492]'}`;
  const netVal = (r) => (unit === 'R' ? rMult(r.lockedR, true) : moneyPk(r.lockedMoney, true));
  const stopVal = (r) => (unit === 'R' ? rMult(r.ifStopR, true) : moneyPk(r.ifStopMoney, true));
  return (
    <>
      <div className="flex items-center gap-2 text-[#5b6573] text-[8px] uppercase tracking-[0.14em] font-medium mt-2.5 mb-1">
        <span className="shrink-0 flex items-center gap-1">Exits<InfoHint text="Scale-out plan. Gross = the gross R the price travels to the optimized target (fees in). Net = the net result you secure by closing through that TP. ↩ St = net result if price then returns to the stop. Allocation % is set in Settings. Toggle R / $ for the result columns." /></span>
        <span className="flex-1 h-px bg-white/[0.07]" />
        <span className="flex items-center normal-case tracking-normal shrink-0">
          <button onClick={() => setUnit('R')} className={seg('R')}>R</button>
          <span className="text-[#3a424d]">/</span>
          <button onClick={() => setUnit('$')} className={seg('$')}>$</button>
        </span>
        <span className="pk-mono text-[10px] normal-case tracking-normal" style={{ color: NET }}>{rMult(data.blendedNetRR)}</span>
      </div>
      <div className={`${GRID6} text-[8px] uppercase tracking-[0.06em] text-[#5b6573] font-semibold pb-1`}>
        <span>TP</span>
        <span className="text-right">Qty</span>
        <span>Target</span>
        <span className="text-right">Gross</span>
        <span className="text-right border-l border-white/[0.06] pl-1">Net</span>
        <span className="text-right">↩St</span>
      </div>
      {data.rows.map((r, i) => (
        <div key={i} className={`${GRID6} py-[3px] text-[10.5px]`}>
          <span className="text-[8px] text-[#5b6573] font-semibold tracking-[0.06em]">TP{i + 1}</span>
          <span onClick={() => cpNum(r.qty)} title="Copy qty" className={`pk-mono text-[#7a8492] text-right ${TAP}`}>{qtyFmtPk(r.qty)}</span>
          <span onClick={() => cpNum(r.targetPrice)} title="Copy target" className={`pk-mono whitespace-nowrap overflow-hidden ${TAP}`} style={{ color: NET }}>{pricePrefix}{priceFmt(r.targetPrice, decimals)}</span>
          <span onClick={() => cpNum(r.distR)} title="Copy gross R" className={`pk-mono text-[#7a8492] text-right ${TAP}`}>{rMult(r.distR)}</span>
          <span className="pk-mono text-right border-l border-white/[0.06] pl-1" style={{ color: NET }}>{netVal(r)}</span>
          <span className="pk-mono text-right" style={{ color: new BigNumber(r.ifStopR).gte(0) ? NET : '#f2767f' }}>{stopVal(r)}</span>
        </div>
      ))}
      {runner.gt(0.0005) && (
        <div className="text-[#5b6573] text-[9px] pt-0.5">RUN · {runner.times(100).toFormat(0)}% rides the remainder</div>
      )}
    </>
  );
};
